/**
 * @fileoverview Persistent storage for PDF text highlights, scoped per PDF URL.
 * Keeps the most-recently-seen N URLs; older ones are evicted on save.
 * @module ui/PdfHighlightStore
 */


const STORAGE_KEY = 'cbmap:pdfHighlights';
const MAX_FILES = 10;
// sanity bounds for records arriving from a file (a highlight import or a settings backup)
const MAX_URL_LENGTH = 4000;
const MAX_ID_LENGTH = 200;
const MAX_LABEL_LENGTH = 2000;
const MAX_TEXT_LENGTH = 100000;
// A highlight's position is a page number and a range of pdf.js text items on it, and drawing it
// walks every item index in the range. A shared file with a range of a billion items ran the tab
// out of memory, and since the record was saved first, it crashed again on every visit (second
// code study 12.1, Oct 2026). Real highlights span a few dozen items at most.
const MAX_PAGE_NUMBER = 100000;
const MAX_ITEM_INDEX = 1000000;
const MAX_HIGHLIGHT_ITEM_SPAN = 5000;


/**
 * The store's entry for a url as an OWN property of store.files only. The url key is whatever string
 * the pdf was loaded by, and a bare files[url] for "__proto__" or "constructor" would hand back
 * Object.prototype or the Object function -- which saveForUrl would then write highlights onto.
 * @param {Object} store - The store ({files: {...}})
 * @param {string} url - The url key
 * @returns {Object|undefined} The entry, or undefined
 */
function getOwnFileEntry(store, url) {
    const files = store && store.files;
    if (!files || typeof files !== 'object' || !Object.prototype.hasOwnProperty.call(files, url)) {
        return undefined;
    }
    const entry = files[url];
    return (entry && typeof entry === 'object' && !Array.isArray(entry)) ? entry : undefined;
}


/**
 * Stores an entry under a url as an own property (an assignment to files["__proto__"] would replace
 * the dictionary's prototype instead of adding an entry).
 * @param {Object} store - The store ({files: {...}})
 * @param {string} url - The url key
 * @param {Object} entry - The entry
 */
function setOwnFileEntry(store, url, entry) {
    Object.defineProperty(store.files, url, { value: entry, writable: true, enumerable: true, configurable: true });
}


/**
 * Persists per-URL highlight lists in localStorage, with LRU eviction
 * by lastSeen timestamp when the file count exceeds MAX_FILES.
 * @class
 */
export class PdfHighlightStore {

    /** @returns {string} The localStorage key the store lives under */
    static get STORAGE_KEY() {
        return STORAGE_KEY;
    }


    /**
     * Validates and normalizes one raw highlight / note record that came from a file (the highlights
     * import, or a settings backup). Returns a clean record, or null if it is missing the required
     * positional fields / text or a field has the wrong type or size.
     * @param {Object} raw
     * @returns {Object|null}
     */
    static sanitizeHighlight(raw) {
        if (!raw || typeof raw !== 'object') return null;
        const num = (v) => (typeof v === 'number' && Number.isFinite(v)) ? v : null;
        // a whole number from 0 to max (positions: page numbers, text item indexes, character offsets)
        const wholeNumber = (v, max) => (Number.isInteger(v) && v >= 0 && v <= max) ? v : null;
        const id = (typeof raw.id === 'string' && raw.id && raw.id.length <= MAX_ID_LENGTH) ? raw.id : null;
        const bookmarkLabel = (typeof raw.bookmarkLabel === 'string' && raw.bookmarkLabel.length <= MAX_LABEL_LENGTH) ? raw.bookmarkLabel : null;
        if (typeof raw.text !== 'string' || raw.text.length > MAX_TEXT_LENGTH) return null;
        const pageNum = wholeNumber(raw.pageNum, MAX_PAGE_NUMBER);
        if (pageNum === null || pageNum < 1) return null;

        // Notes have no positional anchors — validate/normalize them on their own branch.
        if (raw.kind === 'note') {
            const notePage = pageNum;
            return {
                id,
                kind: 'note',
                pageNum: notePage,
                text: raw.text,
                pdfY: null,
                pdfX: null,
                bookmarkLabel,
                createdAt: num(raw.createdAt) || Date.now()
            };
        }

        const startItemIdx = wholeNumber(raw.startItemIdx, MAX_ITEM_INDEX);
        const startOffset = wholeNumber(raw.startOffset, MAX_TEXT_LENGTH);
        const endItemIdx = wholeNumber(raw.endItemIdx, MAX_ITEM_INDEX);
        const endOffset = wholeNumber(raw.endOffset, MAX_TEXT_LENGTH);
        if (startItemIdx === null || startOffset === null || endItemIdx === null || endOffset === null) {
            return null;
        }
        // in order, and no wider than a real highlight could be (see MAX_HIGHLIGHT_ITEM_SPAN)
        if (endItemIdx < startItemIdx || (endItemIdx - startItemIdx) > MAX_HIGHLIGHT_ITEM_SPAN) {
            return null;
        }
        if (raw.text.length === 0) return null;
        return {
            id,
            // the app writes kind 'highlight' on these (only 'note' changes behaviour; kept so a
            // backup round trip returns the record unchanged)
            ...(raw.kind === 'highlight' && { kind: 'highlight' }),
            pageNum,
            startItemIdx,
            startOffset,
            endItemIdx,
            endOffset,
            text: raw.text,
            pdfY: num(raw.pdfY),
            pdfX: num(raw.pdfX),
            bookmarkLabel,
            createdAt: num(raw.createdAt) || Date.now()
        };
    }


    /**
     * Validates a whole stored highlight store (the value kept under STORAGE_KEY) as found in a
     * settings backup: keeps only well-formed per-url entries, and in them only well-formed highlights
     * that carry an id (stored highlights are looked up by id).
     * @param {*} value - The parsed value
     * @returns {{store: Object, droppedCount: number}|null} The clean store and how many highlights /
     *   entries were dropped, or null if the value is not a store at all
     */
    static sanitizeStoreValue(value) {
        if (!value || typeof value !== 'object' || Array.isArray(value) || !value.files || typeof value.files !== 'object' || Array.isArray(value.files)) {
            return null;
        }
        const num = (v) => (typeof v === 'number' && Number.isFinite(v)) ? v : null;
        const store = { files: {} };
        let droppedCount = 0;
        for (const [url, entry] of Object.entries(value.files)) {
            // (a url key named like an Object.prototype member is never a pdf the app stored)
            if (!url || url.length > MAX_URL_LENGTH || (url in Object.prototype) || !entry || typeof entry !== 'object' || Array.isArray(entry)) {
                droppedCount++;
                continue;
            }
            const highlights = [];
            for (const raw of (Array.isArray(entry.highlights) ? entry.highlights : [])) {
                const clean = PdfHighlightStore.sanitizeHighlight(raw);
                if (clean && clean.id) {
                    highlights.push(clean);
                } else {
                    droppedCount++;
                }
            }
            const cleanEntry = { highlights };
            if (num(entry.lastPage) !== null) cleanEntry.lastPage = entry.lastPage;
            if (num(entry.lastSeen) !== null) cleanEntry.lastSeen = entry.lastSeen;
            setOwnFileEntry(store, url, cleanEntry);
        }
        return { store, droppedCount };
    }


    /**
     * Loads the highlight list for a given URL and marks the URL as seen now.
     * @param {string} url
     * @returns {Array<Object>} highlights (empty if none)
     */
    loadForUrl(url) {
        if (!url) return [];
        const store = this._readStore();
        const entry = getOwnFileEntry(store, url);
        if (!entry || !Array.isArray(entry.highlights)) return [];
        entry.lastSeen = Date.now();
        this._writeStore(store);
        return entry.highlights;
    }


    /**
     * Saves the highlight list for a given URL, touching its lastSeen and
     * pruning the store to MAX_FILES entries (LRU by lastSeen). Passing an
     * empty list removes the URL entry entirely — unless a last-viewed page is
     * stored on it, in which case the entry is kept (with empty highlights).
     * @param {string} url
     * @param {Array<Object>} highlights
     */
    saveForUrl(url, highlights) {
        if (!url) return;
        const store = this._readStore();
        const entry = getOwnFileEntry(store, url) || {};
        entry.highlights = (highlights && highlights.length) ? highlights : [];
        entry.lastSeen = Date.now();
        if (entry.highlights.length === 0 && typeof entry.lastPage !== 'number') {
            // nothing worth keeping for this url
            delete store.files[url];
        } else {
            setOwnFileEntry(store, url, entry);
        }
        this._pruneStore(store);
        this._writeStore(store);
    }


    /**
     * Returns the saved last-viewed page for a URL, or null if none is stored.
     * @param {string} url
     * @returns {number|null}
     */
    loadPageForUrl(url) {
        if (!url) return null;
        const store = this._readStore();
        const entry = getOwnFileEntry(store, url);
        return (entry && typeof entry.lastPage === 'number') ? entry.lastPage : null;
    }


    /**
     * Saves the last-viewed page for a URL, kept alongside highlights in the same per-file entry
     * (so it survives even when there are no highlights). Touches lastSeen and prunes.
     * @param {string} url
     * @param {number} page
     */
    savePageForUrl(url, page) {
        if (!url || typeof page !== 'number') return;
        const store = this._readStore();
        const entry = getOwnFileEntry(store, url) || { highlights: [] };
        if (!Array.isArray(entry.highlights)) entry.highlights = [];
        entry.lastPage = page;
        entry.lastSeen = Date.now();
        setOwnFileEntry(store, url, entry);
        this._pruneStore(store);
        this._writeStore(store);
    }


    /** @private */
    _readStore() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return { files: {} };
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object' || !parsed.files || typeof parsed.files !== 'object' || Array.isArray(parsed.files)) {
                return { files: {} };
            }
            return parsed;
        } catch (e) {
            console.warn('PdfHighlightStore: failed to read store, resetting', e);
            return { files: {} };
        }
    }


    /** @private */
    _writeStore(store) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        } catch (e) {
            console.warn('PdfHighlightStore: failed to write store', e);
        }
    }


    /** @private */
    _pruneStore(store) {
        const entries = Object.entries(store.files);
        if (entries.length <= MAX_FILES) return;
        entries.sort((a, b) => (b[1].lastSeen || 0) - (a[1].lastSeen || 0));
        store.files = {};
        for (let i = 0; i < MAX_FILES; i++) {
            setOwnFileEntry(store, entries[i][0], entries[i][1]);
        }
    }
}
